import { FC } from 'react';
import {
  Box,
  SimpleGrid,
  Image,
  Heading,
  Text,
  Badge,
  HStack,
  VStack,
  Progress,
  useColorModeValue,
  Skeleton,
  SkeletonText,
  Button,
  Icon,
} from '@chakra-ui/react';
import { Course } from '../../services/university-service';
import { AnimatedCard, containerVariants, cardVariants } from '@bemap/ui';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ANALYST_ROUTES } from '@bemap/utils';
import { MdArrowForward } from 'react-icons/md';
import { useTranslation } from 'react-i18next';

const MotionBox = motion(Box);

interface CourseGridProps {
  courses: Course[];
  onCourseClick?: (course: Course) => void;
  loading?: boolean;
  columns?: { base: number; md: number; lg: number };
  spacing?: number;
  title?: string;
}

const CourseGrid: FC<CourseGridProps> = ({
  courses,
  loading = false,
  columns = { base: 1, md: 2, lg: 3 },
  spacing = 6,
  title = ""
}) => {
  const navigate = useNavigate();
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'white');
  const { t } = useTranslation();

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'beginner': return 'green';
      case 'intermediate': return 'orange';
      case 'advanced': return 'red';
      default: return 'gray';
    }
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}min`;
    }
    return `${mins}min`;
  };

  const handleCourseClick = (course: Course) => {
    const courseViewUrl = ANALYST_ROUTES.UNIVERSITY_COURSES_VIEW.replace(':courseId', course.id.toString());
    navigate(courseViewUrl);
  };

  if (loading) {
    return (
      <MotionBox
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        p={6}
      >
        <Heading size="lg" mb={6} color={textColor}>{title || t('university.courses.available_courses')}</Heading>
        <SimpleGrid columns={columns} spacing={spacing}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <AnimatedCard key={i} bg={cardBg} variants={cardVariants}>
              <Skeleton height="200px" mb={4} />
              <SkeletonText noOfLines={2} mb={4} />
              <Skeleton height="20px" width="60%" />
            </AnimatedCard>
          ))}
        </SimpleGrid>
      </MotionBox>
    );
  }

  return (
    <MotionBox
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      p={6}
    >
      <Heading size="lg" mb={6} color={textColor}>{title || t('university.courses.available_courses')}</Heading>
      <SimpleGrid columns={columns} spacing={spacing}>
        {courses.map((course) => (
          <AnimatedCard
            key={course.id}
            bg={cardBg}
            cursor="pointer"
            transition="all 0.2s"
            _hover={{
              transform: 'translateY(-2px)',
              shadow: 'lg',
              borderColor: 'blue.300',
            }}
            onClick={() => handleCourseClick(course)}
            variants={cardVariants}
          >
            {course.thumbnailUrl && (
              <Image
                src={course.thumbnailUrl}
                alt={course.title}
                borderRadius="md"
                mb={4}
                height="200px"
                objectFit="cover"
                fallbackSrc="https://placehold.co/400x200"
              />
            )}

            <VStack align="stretch" spacing={3}>
              <HStack justify="space-between">
                <Badge colorScheme={getLevelColor(course.level)} size="sm">
                  {course.level}
                </Badge>
                <Text fontSize="sm" color="gray.500">
                  {formatDuration(course.durationMinutes)}
                </Text>
              </HStack>

              <Heading size="md" color={textColor} noOfLines={2}>
                {course.title}
              </Heading>

              <Text fontSize="sm" color="gray.600" noOfLines={2}>
                {course.description}
              </Text>

              <Text fontSize="sm" color="blue.600" fontWeight="medium">
                {course.instructor}
              </Text>

              <VStack spacing={2} align="stretch">
                <HStack justify="space-between">
                  <Text fontSize="sm" color="gray.500">
                    {t('university.courses.progress')}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    {course.progress}%
                  </Text>
                </HStack>
                <Progress
                  value={course.progress}
                  colorScheme="blue"
                  size="sm"
                  borderRadius="full"
                />
              </VStack>

              {course.completed && (
                <Badge colorScheme="green" alignSelf="flex-start">
                  {t('university.students.status.completed')}
                </Badge>
              )}

              {/* Action Button */}
              <Button
                size="sm"
                colorScheme="blue"
                variant="outline"
                rightIcon={<Icon as={MdArrowForward} />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCourseClick(course);
                }}
                _hover={{
                  bg: 'blue.50',
                  borderColor: 'blue.400',
                }}
              >
                {t('university.students.view_details')}
              </Button>
            </VStack>
          </AnimatedCard>
        ))}
      </SimpleGrid>

      {courses.length === 0 && !loading && (
        <Box textAlign="center" py={12}>
          <Text color="gray.500" fontSize="lg">
            {t('university.courses.no_courses_available')}
          </Text>
        </Box>
      )}
    </MotionBox>
  );
};

export default CourseGrid; 